import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  addCulture,
  deleteCulture,
  deleteCultureMappingData,
  deleteGroundTypeMapping,
  getCultures,
  resetCulture,
} from 'services/API/Cooperative';
import { ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { DialogContentText } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { displayErrorMessage } from 'utils/tools_functions';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { resetEffects } from 'services/API/Exploitation';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { useTranslation } from 'react-i18next';
import { StyledTooltip, resetErrorsForm } from './CustomGenericDataGridUtils';

const useDataGridMappingActions = (
  rows,
  cooperativeId,
  refreshDataAfterNewFileImport,
  allMappingData,
  from = 'mappingCulture',
) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [actionId, setActionId] = useState(null);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteEffectsDialog, setOpenDeleteEffectsDialog] = useState(false);

  const { t } = useTranslation();

  const handleSaveClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View },
    });
  };

  const resetRowModesModel = () => {
    setRowModesModel((prevRowModesModel) => {
      let resetPrevRowModesModel = {};
      for (const key in prevRowModesModel) {
        if (prevRowModesModel.hasOwnProperty(key)) {
          resetPrevRowModesModel[key] = { mode: GridRowModes.View, ignoreModifications: true };
        }
      }
      return { ...resetPrevRowModesModel };
    });
  };

  const handleEditClick = (id) => () => {
    setRowModesModel((prevRowModesModel) => {
      if (Object.keys(prevRowModesModel).length === 0) {
        return { [id]: { mode: GridRowModes.Edit } };
      } else {
        let resetPrevRowModesModel = {};
        for (const key in prevRowModesModel) {
          if (prevRowModesModel.hasOwnProperty(key)) {
            resetPrevRowModesModel[key] = { mode: GridRowModes.View };
          }
        }
        return { ...resetPrevRowModesModel, [id]: { mode: GridRowModes.Edit, fromAction: true } };
      }
    });
  };

  const handleCancelClick = (id) => (field) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const handleDeleteDialogOpen = async (id) => {
    setActionId(id);
    setOpenDeleteDialog(true);
  };

  const getActions = ({ id }) => {
    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    if (isInEditMode) {
      return [
        <div>
          <StyledTooltip placement="top" title={t('common.save')}>
            <GridActionsCellItem
              icon={<SaveIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.save')}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />
          </StyledTooltip>
          <StyledTooltip placement="top" title={t('common.cancel')}>
            <GridActionsCellItem
              icon={<CancelIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.cancel')}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          </StyledTooltip>
        </div>,
      ];
    }
    return [
      <div>
        <StyledTooltip placement="top" title={t('action.edit')}>
          <GridActionsCellItem
            icon={<EditIcon sx={{ paddingLeft: '2px !important' }} />}
            label={t('action.edit')}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        </StyledTooltip>
        <StyledTooltip placement="top" title={t('common.delete')}>
          <GridActionsCellItem
            icon={<DeleteIcon sx={{ paddingLeft: '2px !important' }} />}
            label={t('common.delete')}
            onClick={() => handleDeleteDialogOpen(id)}
            color="inherit"
          />
        </StyledTooltip>
      </div>,
    ];
  };

  const handleDeleteClick = async () => {
    setOpenDeleteDialog(false);
    const rowToDelete = rows.find((row) => row.id === actionId);
    const rowToDeleteMappingType = rowToDelete.mappingType;
    try {
      if (from === 'mappingCulture') {
        await deleteCultureMappingData(rowToDeleteMappingType, cooperativeId, {
          id: rowToDelete.id,
        });
      } else {
        await deleteGroundTypeMapping(rowToDeleteMappingType, cooperativeId, {
          id: rowToDelete.id,
        });
      }
      toast.success(t('common.itemDeleted', { name: rowToDelete.externalName }));
      refreshDataAfterNewFileImport(
        rowToDeleteMappingType,
        allMappingData?.[rowToDeleteMappingType]?.filter(
          (mappingData) => mappingData.id !== rowToDelete.id,
        ),
      );
    } catch (error) {
      toast.error(displayErrorMessage('ERR_DELETE_MAPPING_DATA'));
    }
  };

  const RenderDeleteDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            {from === 'mappingCulture'
              ? t('dialog.confirmDeleteCulture')
              : t('dialog.confirmDeleteMappingGroundType')}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return {
    rowModesModel,
    setRowModesModel,
    getActions,
    RenderDeleteDialog,
    resetRowModesModel,
  };
};

export default useDataGridMappingActions;
